import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Phone,
  PhoneOff,
  CheckCircle2,
  AlertCircle,
  Clock,
  X,
  Calendar,
  PhoneCall,
  BarChart3,
  FileText,
  ListChecks,
  Mic,
  Timer,
  PhoneIncoming,
  CalendarCheck,
  PhoneMissed,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import {
  useVoiceCalls,
  useVoiceCall,
  useVoiceBatch,
  useCancelVoiceCall,
  useVoiceStats,
  type VoiceCallRow,
  type VoiceCallStatus,
  type VoiceCallOutcome,
} from '@/hooks/data/useVoiceCalls';
import { useClients } from '@/hooks/data/useClients';
import { fetchMe } from '@/lib/auth';
import { formatDateTime, formatNumber } from '@/lib/formatLocale';

// v2 — Virtuelle Sekretärin (Sophie) is restricted to a single
// operator (admin1 / Zoran Kalabić). Other admins are silently
// redirected to the dashboard. The matching backend gate is in
// apps/api/src/routes/voice.ts (requireVoiceOperator middleware).
export const Route = createFileRoute('/_app/voice-calls')({
  beforeLoad: async () => {
    const me = await fetchMe();
    if (!me || me.profile?.role !== 'admin' || me.profile?.agent_id !== 'admin1') {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: VoiceCallsPage,
});

const STATUS_PILL: Record<VoiceCallStatus, string> = {
  queued: 'fm-pill-neutral',
  calling: 'fm-pill-warn',
  completed: 'fm-pill-pos',
  failed: 'fm-pill-dgr',
  no_answer: 'fm-pill-neutral',
  cancelled: 'fm-pill-neutral',
};

const OUTCOME_PILL: Record<VoiceCallOutcome, string> = {
  termin_scheduled: 'fm-pill-pos',
  not_interested: 'fm-pill-neutral',
  callback_requested: 'fm-pill-info',
  wrong_number: 'fm-pill-dgr',
  language_barrier: 'fm-pill-warn',
  voicemail_left: 'fm-pill-neutral',
};

function fmtDuration(s: number | null): string {
  if (!s) return '—';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

type Tab = 'anrufliste' | 'berichte' | 'transkripte';

function VoiceCallsPage() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const [tab, setTab] = useState<Tab>('anrufliste');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterCallType, setFilterCallType] = useState<string>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Anrufliste pool — clients imported from Abgeber Liste.xlsx (server
  // tags them with platform='Abgeber Liste' or 'Abgeber Liste (N)').
  // 25 rows per page; checkboxes drive the batch caller below.
  const POOL_PAGE_SIZE = 25;
  const [poolPage, setPoolPage] = useState(0);
  const [poolSearch, setPoolSearch] = useState('');
  const [poolSelected, setPoolSelected] = useState<Set<string>>(new Set());
  const [batchType, setBatchType] = useState<
    'private_seller_prospect' | 'interessent_followup'
  >('interessent_followup');
  const { data: poolClients = [] } = useClients({
    audience: 'imported',
    limit: POOL_PAGE_SIZE,
    offset: poolPage * POOL_PAGE_SIZE,
    ...(poolSearch.trim() ? { search: poolSearch.trim() } : {}),
  });
  const togglePool = (id: string) =>
    setPoolSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const batch = useVoiceBatch();
  const launchBatch = async () => {
    if (poolSelected.size === 0) return;
    try {
      const recipients = Array.from(poolSelected).map((id) => ({ client_id: id }));
      const r = await batch.mutateAsync({ call_type: batchType, recipients });
      const calling = r.results.filter((x) => x.status === 'calling').length;
      const skipped = r.results.filter((x) => x.status === 'skipped').length;
      const failed = r.results.filter((x) => x.status === 'failed').length;
      toast.success(
        `${calling} Anruf(e) gestartet${skipped ? ` · ${skipped} übersprungen` : ''}${failed ? ` · ${failed} fehler` : ''}`,
      );
      setPoolSelected(new Set());
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  const { data: calls = [], isLoading } = useVoiceCalls({
    status: filterStatus || undefined,
    call_type: filterCallType || undefined,
  });

  if (profile?.role !== 'admin' || profile?.agent_id !== 'admin1') return null;

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 font-display text-2xl text-ink">
            <PhoneCall className="h-6 w-6 text-ink-3" />
            {t('voice.title', 'Virtuelle Sekretärin')}
          </h2>
          <p className="text-sm text-ink-3">
            {t(
              'voice.subtitle',
              'AI-Sprachagent — automatische Erstanrufe an Verkäufer und Interessenten.',
            )}
          </p>
        </div>
      </header>

      {/* Tab strip — split the operator workflow (Anrufliste) from the
          reporting surfaces (Berichte KPIs + Transkripte history). */}
      <div className="flex flex-wrap gap-1 border-b border-line">
        <TabButton active={tab === 'anrufliste'} onClick={() => setTab('anrufliste')} icon={<ListChecks className="h-4 w-4" />}>
          {t('voice.tab.anrufliste', 'Anrufliste')}
        </TabButton>
        <TabButton active={tab === 'berichte'} onClick={() => setTab('berichte')} icon={<BarChart3 className="h-4 w-4" />}>
          {t('voice.tab.berichte', 'Berichte')}
        </TabButton>
        <TabButton active={tab === 'transkripte'} onClick={() => setTab('transkripte')} icon={<FileText className="h-4 w-4" />}>
          {t('voice.tab.transkripte', 'Transkripte & Verlauf')}
        </TabButton>
      </div>

      {tab === 'berichte' && <BerichtePanel onOpenCall={setSelectedId} />}

      {/* Anrufliste pool — paginated, search, multi-select. Pool source
          is `audience=imported` (Abgeber Liste.xlsx). */}
      {tab === 'anrufliste' && (
      <div className="fm-card p-5 space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 font-display text-lg text-ink">
              <Phone className="h-4 w-4 text-ink-3" />
              {t('voice.pool.title', 'Anrufliste — Abgeber Liste')}
            </h3>
            <p className="text-xs text-ink-3">
              {t(
                'voice.pool.subtitle',
                'Klienten aus dem Import. Sophie ruft die markierten Nummern automatisch an.',
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={batchType}
              onChange={(e) =>
                setBatchType(
                  e.target.value as
                    | 'private_seller_prospect'
                    | 'interessent_followup',
                )
              }
              className="rounded-md border border-line bg-card px-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
            >
              <option value="interessent_followup">
                {t('voice.type.followup', 'Interessent-Follow-up')}
              </option>
              <option value="private_seller_prospect">
                {t('voice.type.prospect', 'Verkäufer-Erstkontakt')}
              </option>
            </select>
            <Button
              type="button"
              onClick={launchBatch}
              disabled={poolSelected.size === 0 || batch.isPending}
            >
              <PhoneCall className="me-1.5 h-4 w-4" />
              {batch.isPending
                ? t('voice.pool.starting', 'Anrufe werden gestartet …')
                : t('voice.pool.start', 'Charge anrufen')}
              {poolSelected.size > 0 ? ` (${poolSelected.size})` : ''}
            </Button>
          </div>
        </div>

        <input
          type="search"
          value={poolSearch}
          onChange={(e) => {
            setPoolSearch(e.target.value);
            setPoolPage(0);
          }}
          placeholder={t('voice.pool.search', 'Suchen — Name, Telefon …')}
          className="w-full rounded-md border border-line bg-card px-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
        />

        {poolClients.length === 0 ? (
          <p className="py-8 text-center text-sm text-ink-3">
            {t('voice.pool.empty', 'Keine Klienten in der Anrufliste auf dieser Seite.')}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-[11px] uppercase tracking-wider text-ink-3">
                    <th className="w-8 py-2"></th>
                    <th className="px-2 py-2 text-start">{t('clients.cols.name', 'Name')}</th>
                    <th className="px-2 py-2 text-start">{t('clients.cols.phone', 'Telefon')}</th>
                    <th className="px-2 py-2 text-start">{t('clients.cols.type', 'Typ')}</th>
                    <th className="px-2 py-2 text-start">{t('clients.cols.platform', 'Plattform')}</th>
                  </tr>
                </thead>
                <tbody>
                  {poolClients.map((c) => {
                    const fullName = [c.title, c.first_name, c.last_name].filter(Boolean).join(' ') || '—';
                    return (
                      <tr key={c.id} className="border-b border-line/50">
                        <td className="py-2 text-center">
                          <input
                            type="checkbox"
                            checked={poolSelected.has(c.id)}
                            onChange={() => togglePool(c.id)}
                            className="cursor-pointer"
                          />
                        </td>
                        <td className="px-2 py-2 text-ink">{fullName}</td>
                        <td className="px-2 py-2 font-mono text-xs text-ink-2">{c.phone}</td>
                        <td className="px-2 py-2 text-ink-2">
                          {t(`enums.client_type.${c.client_type}`, c.client_type)}
                        </td>
                        <td className="px-2 py-2 text-xs text-ink-3">{c.platform ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between text-xs text-ink-3">
              <span>
                {t('voice.pool.page', 'Seite')} {poolPage + 1} ·{' '}
                {t('voice.pool.shown', 'angezeigt')} {poolClients.length}
                {poolSelected.size > 0 && ` · ${poolSelected.size} ${t('voice.pool.selected', 'markiert')}`}
              </span>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setPoolPage((p) => Math.max(0, p - 1))}
                  disabled={poolPage === 0}
                  className="rounded-md border border-line bg-card px-3 py-1.5 text-xs font-semibold text-ink-2 transition hover:bg-paper-2 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  ← {t('common.prev', 'Zurück')}
                </button>
                <button
                  type="button"
                  onClick={() => setPoolPage((p) => p + 1)}
                  disabled={poolClients.length < POOL_PAGE_SIZE}
                  className="rounded-md border border-line bg-card px-3 py-1.5 text-xs font-semibold text-ink-2 transition hover:bg-paper-2 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {t('common.next', 'Weiter')} →
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      )}

      {tab === 'transkripte' && (
      <div className="fm-card p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="rounded-md border border-line bg-card px-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
          >
            <option value="">{t('voice.filter.all_status', 'Alle Status')}</option>
            <option value="queued">Queued</option>
            <option value="calling">Calling</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="no_answer">No answer</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            value={filterCallType}
            onChange={(e) => setFilterCallType(e.target.value)}
            className="rounded-md border border-line bg-card px-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
          >
            <option value="">{t('voice.filter.all_types', 'Alle Typen')}</option>
            <option value="private_seller_prospect">{t('voice.type.prospect', 'Verkäufer-Erstkontakt')}</option>
            <option value="interessent_followup">{t('voice.type.followup', 'Interessent-Follow-up')}</option>
          </select>
        </div>

        {isLoading ? (
          <p className="py-8 text-center text-sm text-ink-3">{t('common.loading', 'Lädt …')}</p>
        ) : calls.length === 0 ? (
          <div className="py-12 text-center">
            <Phone className="mx-auto mb-3 h-10 w-10 text-line-strong" />
            <p className="text-sm font-medium text-ink-2">
              {t('voice.empty.title', 'Noch keine Anrufe.')}
            </p>
            <p className="text-xs text-ink-3">
              {t(
                'voice.empty.body',
                'Wählen Sie Klienten in /clients oder /properties aus und starten Sie eine Anruf-Charge.',
              )}
            </p>
          </div>
        ) : (
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-y border-line bg-paper-2 text-[11px] font-semibold uppercase tracking-wider text-ink-3">
                  <th className="px-5 py-2 text-start">{t('voice.col.created', 'Zeit')}</th>
                  <th className="px-2 py-2 text-start">{t('voice.col.recipient', 'Empfänger')}</th>
                  <th className="px-2 py-2 text-start">{t('voice.col.type', 'Typ')}</th>
                  <th className="px-2 py-2 text-start">{t('voice.col.status', 'Status')}</th>
                  <th className="px-2 py-2 text-start">{t('voice.col.outcome', 'Ergebnis')}</th>
                  <th className="px-2 py-2 text-end">{t('voice.col.duration', 'Dauer')}</th>
                  <th className="px-2 py-2 text-end">{t('voice.col.cost', 'Kosten')}</th>
                  <th className="px-2 py-2 text-start">{t('voice.col.initiator', 'Gestartet von')}</th>
                </tr>
              </thead>
              <tbody>
                {calls.map((c) => (
                  <CallRow key={c.id} row={c} onSelect={() => setSelectedId(c.id)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {selectedId && (
        <CallDrawer id={selectedId} onClose={() => setSelectedId(null)} />
      )}
    </section>
  );
}

// ─── Tab strip button ─────────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
        active
          ? 'border-[hsl(var(--brand-gold))] text-ink'
          : 'border-transparent text-ink-3 hover:text-ink-2'
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

// ─── Berichte panel — KPI cards + outcome distribution + daily sparkline

const RANGE_PRESETS: Array<{ id: string; days: number; label: string }> = [
  { id: '7d', days: 7, label: 'Letzte 7 Tage' },
  { id: '30d', days: 30, label: 'Letzte 30 Tage' },
  { id: '90d', days: 90, label: 'Letzte 90 Tage' },
];

function BerichtePanel({ onOpenCall }: { onOpenCall: (id: string) => void }) {
  const { t } = useTranslation();
  const [rangeId, setRangeId] = useState<string>('30d');
  const range = RANGE_PRESETS.find((r) => r.id === rangeId) ?? RANGE_PRESETS[1]!;
  // useMemo so `Date.now()` doesn't recompute on every render → React Query
  // would otherwise see a new query key per render and never settle.
  const { fromMs, toMs } = useMemo(() => {
    const to = Date.now();
    return { fromMs: to - range.days * 24 * 60 * 60 * 1000, toMs: to };
  }, [range.days]);
  const { data: stats, isLoading } = useVoiceStats(fromMs, toMs);
  const { data: latestCalls = [] } = useVoiceCalls({ status: '' });

  if (isLoading || !stats) {
    return (
      <div className="fm-card p-8 text-center text-sm text-ink-3">
        {t('common.loading', 'Lädt …')}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Range picker */}
      <div className="flex flex-wrap items-center gap-2">
        {RANGE_PRESETS.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => setRangeId(r.id)}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              rangeId === r.id
                ? 'bg-ink text-paper'
                : 'border border-line bg-card text-ink-2 hover:bg-paper-2'
            }`}
          >
            {t(`voice.range.${r.id}`, r.label)}
          </button>
        ))}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          icon={<PhoneCall className="h-4 w-4" />}
          label={t('voice.kpi.total', 'Gesamtanrufe')}
          value={formatNumber(stats.total)}
        />
        <KpiCard
          icon={<CalendarCheck className="h-4 w-4 text-[hsl(var(--positive))]" />}
          label={t('voice.kpi.termine', 'Termine vereinbart')}
          value={formatNumber(stats.by_outcome.termin_scheduled)}
          accent
        />
        <KpiCard
          icon={<Timer className="h-4 w-4" />}
          label={t('voice.kpi.avg_duration', 'Ø Dauer')}
          value={fmtDuration(Math.round(stats.avg_seconds)) || '—'}
        />
        <KpiCard
          icon={<BarChart3 className="h-4 w-4" />}
          label={t('voice.kpi.cost_total', 'Gesamtkosten')}
          value={`$ ${stats.total_cost_usd.toFixed(2)}`}
          hint={stats.avg_cost_usd > 0 ? `Ø $${stats.avg_cost_usd.toFixed(2)}/Anruf` : undefined}
        />
      </div>

      {/* Outcome distribution + Status distribution side by side */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <DistributionCard
          title={t('voice.berichte.outcomes_title', 'Ergebnisse')}
          subtitle={t('voice.berichte.outcomes_sub', 'Wie haben die Anrufe geendet?')}
          items={[
            { key: 'termin_scheduled', label: t('voice.outcome.termin_scheduled', 'Termin vereinbart'), value: stats.by_outcome.termin_scheduled, color: '#16a34a' },
            { key: 'callback_requested', label: t('voice.outcome.callback_requested', 'Rückruf erbeten'), value: stats.by_outcome.callback_requested, color: '#0ea5e9' },
            { key: 'not_interested', label: t('voice.outcome.not_interested', 'Kein Interesse'), value: stats.by_outcome.not_interested, color: '#6b7280' },
            { key: 'wrong_number', label: t('voice.outcome.wrong_number', 'Falsche Nummer'), value: stats.by_outcome.wrong_number, color: '#dc2626' },
            { key: 'language_barrier', label: t('voice.outcome.language_barrier', 'Sprachbarriere'), value: stats.by_outcome.language_barrier, color: '#f59e0b' },
            { key: 'voicemail_left', label: t('voice.outcome.voicemail_left', 'Voicemail'), value: stats.by_outcome.voicemail_left, color: '#94a3b8' },
          ]}
          icon={<CalendarCheck className="h-4 w-4 text-ink-3" />}
        />
        <DistributionCard
          title={t('voice.berichte.statuses_title', 'Status-Übersicht')}
          subtitle={t('voice.berichte.statuses_sub', 'Anruflieferung — fertig vs. fehlgeschlagen.')}
          items={[
            { key: 'completed', label: 'Completed', value: stats.by_status.completed, color: '#16a34a' },
            { key: 'calling', label: 'Calling (live)', value: stats.by_status.calling, color: '#f59e0b' },
            { key: 'queued', label: 'Queued', value: stats.by_status.queued, color: '#94a3b8' },
            { key: 'no_answer', label: 'No answer', value: stats.by_status.no_answer, color: '#6b7280' },
            { key: 'failed', label: 'Failed', value: stats.by_status.failed, color: '#dc2626' },
            { key: 'cancelled', label: 'Cancelled', value: stats.by_status.cancelled, color: '#475569' },
          ]}
          icon={<PhoneIncoming className="h-4 w-4 text-ink-3" />}
        />
      </div>

      {/* Daily sparkline */}
      <DailySparkline data={stats.by_day} title={t('voice.berichte.by_day', 'Anrufe pro Tag')} />

      {/* Quick access to most recent calls */}
      <div className="fm-card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-display text-base text-ink">
            <FileText className="h-4 w-4 text-ink-3" />
            {t('voice.berichte.recent_title', 'Aktuelle Anrufe')}
          </h3>
          <span className="text-xs text-ink-3">
            {t('voice.berichte.recent_hint', 'Klick öffnet Transkript & Aufnahme')}
          </span>
        </div>
        {latestCalls.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-3">
            {t('voice.empty.title', 'Noch keine Anrufe.')}
          </p>
        ) : (
          <div className="divide-y divide-line">
            {latestCalls.slice(0, 8).map((c) => {
              const name = [c.client_first_name, c.client_last_name].filter(Boolean).join(' ') || c.phone;
              const when = formatDateTime(new Date(c.created_at), {
                dateStyle: 'short',
                timeStyle: 'short',
              });
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onOpenCall(c.id)}
                  className="flex w-full items-center justify-between gap-3 py-2.5 text-start hover:bg-paper-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">{name}</p>
                    <p className="text-[11px] text-ink-3">
                      {when} · {c.phone} ·{' '}
                      <span className="capitalize">{c.status}</span>
                      {c.outcome && ` · ${t(`voice.outcome.${c.outcome}`, c.outcome)}`}
                    </p>
                  </div>
                  <span className="text-xs text-ink-3 whitespace-nowrap tabular-nums">
                    {fmtDuration(c.duration_seconds)}
                    {c.cost_usd != null && c.cost_usd > 0 && (
                      <span className="ms-2 text-ink-3">· $ {c.cost_usd.toFixed(2)}</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  accent,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  accent?: boolean;
  hint?: string;
}) {
  return (
    <div
      className={`fm-card p-4 ${
        accent ? 'border-[hsl(var(--brand-gold))]/40 ring-1 ring-[hsl(var(--brand-gold))]/20' : ''
      }`}
    >
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-ink-3">
        {icon}
        {label}
      </div>
      <p className="mt-1 font-display text-2xl text-ink">{value}</p>
      {hint && <p className="mt-0.5 text-[10px] text-ink-3">{hint}</p>}
    </div>
  );
}

function DistributionCard({
  title,
  subtitle,
  items,
  icon,
}: {
  title: string;
  subtitle: string;
  items: Array<{ key: string; label: string; value: number; color: string }>;
  icon: React.ReactNode;
}) {
  const total = items.reduce((s, x) => s + x.value, 0);
  return (
    <div className="fm-card p-5">
      <div className="mb-3">
        <h3 className="flex items-center gap-2 font-display text-base text-ink">
          {icon}
          {title}
        </h3>
        <p className="text-xs text-ink-3">{subtitle}</p>
      </div>
      {total === 0 ? (
        <p className="py-6 text-center text-sm text-ink-3">— keine Daten —</p>
      ) : (
        <div className="space-y-2.5">
          {items.map((it) => {
            const pct = total > 0 ? (it.value / total) * 100 : 0;
            return (
              <div key={it.key}>
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="text-ink-2">{it.label}</span>
                  <span className="text-ink-3">
                    {it.value} · {pct.toFixed(0)}%
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-paper-2">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${pct}%`, background: it.color }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DailySparkline({
  data,
  title,
}: {
  data: Array<{ day: string; calls: number; termin_scheduled: number }>;
  title: string;
}) {
  const { t } = useTranslation();
  const maxCalls = Math.max(1, ...data.map((d) => d.calls));
  return (
    <div className="fm-card p-5">
      <h3 className="mb-3 flex items-center gap-2 font-display text-base text-ink">
        <BarChart3 className="h-4 w-4 text-ink-3" />
        {title}
      </h3>
      <div className="flex h-32 items-end gap-1">
        {data.map((d) => {
          const totalH = (d.calls / maxCalls) * 100;
          const terminH = d.calls > 0 ? (d.termin_scheduled / d.calls) * totalH : 0;
          return (
            <div
              key={d.day}
              className="group relative flex-1"
              title={t('voice.chart.day_tooltip', '{{day}}: {{calls}} Anrufe ({{termine}} Termine)', { day: d.day, calls: d.calls, termine: d.termin_scheduled })}
            >
              <div
                className="w-full rounded-t-sm bg-paper-3"
                style={{ height: `${Math.max(2, totalH)}%` }}
              >
                <div
                  className="w-full rounded-t-sm bg-[hsl(var(--positive))]"
                  style={{ height: `${terminH}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px] text-ink-3">
        <span>{data[0]?.day ?? ''}</span>
        <span className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-sm bg-paper-3" /> alle Anrufe
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-sm bg-[hsl(var(--positive))]" /> Termine
          </span>
        </span>
        <span>{data[data.length - 1]?.day ?? ''}</span>
      </div>
    </div>
  );
}

function CallRow({ row, onSelect }: { row: VoiceCallRow; onSelect: () => void }) {
  const { t } = useTranslation();
  const name =
    [row.client_first_name, row.client_last_name].filter(Boolean).join(' ') || row.phone;
  const time = formatDateTime(new Date(row.created_at), {
    dateStyle: 'short',
    timeStyle: 'short',
  });
  return (
    <tr className="border-b border-line hover:bg-paper-2 cursor-pointer" onClick={onSelect}>
      <td className="px-5 py-2.5 text-xs text-ink-3 whitespace-nowrap">{time}</td>
      <td className="px-2 py-2.5">
        <p className="font-medium text-ink">{name}</p>
        <p className="text-[11px] text-ink-3">{row.phone}</p>
      </td>
      <td className="px-2 py-2.5 text-xs text-ink-2">
        {row.call_type === 'private_seller_prospect'
          ? t('voice.type.prospect', 'Verkäufer-Erstkontakt')
          : t('voice.type.followup', 'Interessent-Follow-up')}
      </td>
      <td className="px-2 py-2.5">
        <span className={`fm-pill ${STATUS_PILL[row.status]}`}>{row.status}</span>
      </td>
      <td className="px-2 py-2.5">
        {row.outcome ? (
          <span className={`fm-pill ${OUTCOME_PILL[row.outcome]}`}>
            {t(`voice.outcome.${row.outcome}`, row.outcome)}
          </span>
        ) : (
          <span className="text-ink-3">—</span>
        )}
      </td>
      <td className="px-2 py-2.5 text-end tabular-nums">{fmtDuration(row.duration_seconds)}</td>
      <td className="px-2 py-2.5 text-end tabular-nums text-ink-2">
        {row.cost_usd != null && row.cost_usd > 0 ? `$ ${row.cost_usd.toFixed(2)}` : '—'}
      </td>
      <td className="px-2 py-2.5 text-xs text-ink-3">{row.initiator_name ?? '—'}</td>
    </tr>
  );
}

function CallDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { data: call, isLoading } = useVoiceCall(id);
  const cancel = useCancelVoiceCall();

  const transcript: Array<{ role: string; content: string; time?: number }> =
    call?.transcript ? safeParse(call.transcript) : [];

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-ink/30 backdrop-blur-sm" />
      <aside
        className="w-full max-w-lg overflow-y-auto bg-card border-s border-line shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-line bg-card/95 px-5 py-3 backdrop-blur">
          <div className="min-w-0">
            <h3 className="truncate font-display text-lg text-ink">
              {call ? formatHeaderName(call) : t('common.loading', 'Lädt …')}
            </h3>
            <p className="text-xs text-ink-3">{call?.phone}</p>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </header>

        <div className="p-5 space-y-5">
          {isLoading && (
            <p className="text-sm text-ink-3">{t('common.loading', 'Lädt …')}</p>
          )}
          {call && (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Stat
                  icon={<Clock className="h-3.5 w-3.5" />}
                  label={t('voice.col.duration', 'Dauer')}
                  value={fmtDuration(call.duration_seconds)}
                />
                <Stat
                  icon={<BarChart3 className="h-3.5 w-3.5" />}
                  label={t('voice.col.cost', 'Kosten')}
                  value={call.cost_usd != null && call.cost_usd > 0 ? `$ ${call.cost_usd.toFixed(2)}` : '—'}
                />
                <Stat
                  icon={statusIcon(call.status)}
                  label={t('voice.col.status', 'Status')}
                  value={call.status}
                />
                {call.outcome && (
                  <Stat
                    icon={<CheckCircle2 className="h-3.5 w-3.5 text-pos" />}
                    label={t('voice.col.outcome', 'Ergebnis')}
                    value={t(`voice.outcome.${call.outcome}`, call.outcome)}
                  />
                )}
                {call.termin_proposed_at && (
                  <Stat
                    icon={<Calendar className="h-3.5 w-3.5 text-gold" />}
                    label={t('voice.termin_proposed', 'Vorgeschl. Termin')}
                    value={formatDateTime(new Date(call.termin_proposed_at), {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  />
                )}
              </div>

              {call.outcome_notes && (
                <section>
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-3">
                    {t('voice.summary', 'Zusammenfassung')}
                  </h4>
                  <p className="rounded-md border border-line bg-paper-2 p-3 text-sm leading-relaxed text-ink-2">
                    {call.outcome_notes}
                  </p>
                </section>
              )}

              {call.recording_r2_key && (
                <section>
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-3">
                    {t('voice.recording', 'Aufzeichnung')}
                  </h4>
                  <audio
                    controls
                    src={`/voice/recordings/${call.id}`}
                    className="w-full"
                  />
                </section>
              )}

              {transcript.length > 0 && (
                <section>
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-3">
                    {t('voice.transcript', 'Protokoll')}
                  </h4>
                  <ul className="space-y-2">
                    {transcript.map((m, i) => (
                      <li
                        key={i}
                        className={`rounded-md border border-line px-3 py-2 text-xs ${
                          m.role === 'assistant' || m.role === 'bot'
                            ? 'bg-paper-2'
                            : 'bg-card'
                        }`}
                      >
                        <span className="block text-[10px] uppercase tracking-wider text-ink-3">
                          {m.role === 'assistant' || m.role === 'bot'
                            ? t('voice.role.bot', 'Sekretärin')
                            : t('voice.role.customer', 'Kunde')}
                        </span>
                        <span className="text-ink-2">{m.content}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {(call.status === 'calling' || call.status === 'queued') && (
                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => cancel.mutate(call.id)}
                  disabled={cancel.isPending}
                >
                  <PhoneOff className="me-2 h-4 w-4" />
                  {t('voice.cancel', 'Anruf beenden')}
                </Button>
              )}

              {call.error_message && (
                <section className="rounded-md border border-[hsl(var(--danger))]/30 bg-[hsl(var(--danger-bg))] px-3 py-2 text-xs text-[hsl(var(--danger))]">
                  <AlertCircle className="me-1 inline h-3 w-3" />
                  {call.error_message}
                </section>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function Stat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-md border border-line bg-paper-2 p-2.5">
      <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-ink-3">
        {icon}
        {label}
      </p>
      <p className="mt-0.5 text-sm font-medium text-ink">{value}</p>
    </div>
  );
}

function statusIcon(s: VoiceCallStatus) {
  if (s === 'calling') return <Phone className="h-3.5 w-3.5 text-gold animate-pulse" />;
  if (s === 'completed') return <CheckCircle2 className="h-3.5 w-3.5 text-pos" />;
  if (s === 'failed') return <AlertCircle className="h-3.5 w-3.5 text-[hsl(var(--danger))]" />;
  return <Clock className="h-3.5 w-3.5 text-ink-3" />;
}

function formatHeaderName(call: { client_first_name?: string | null; client_last_name?: string | null; phone: string }): string {
  return (
    [call.client_first_name, call.client_last_name].filter(Boolean).join(' ') || call.phone
  );
}

function safeParse<T = unknown>(s: string): T[] {
  try {
    const v = JSON.parse(s);
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}
